/**
 * "Showing N of M customers" — the thing whose absence caused the original problem.
 *
 * Taylor, 2026-08-10: "my one concern with the data in the crm is I don't think it
 * is the full live picture."
 *
 * He was right, and not for the reason I first chased. The daily Domo sync had been
 * running fine; what was wrong is that CRM Home, Follow-ups, Work Queue and
 * Segments each asked /api/customers for `limit=500` against 2,351 customers, then
 * reduced the result into KPIs. So those figures described the first 500 rows while
 * presenting themselves as the whole book — and the endpoint returned
 * `total: customers.length`, so nothing on the page could tell it had been cut off.
 *
 * The caps are raised, which fixes it today. This component is what stops it
 * returning silently when the book outgrows the new limit: a truncated aggregate is
 * not a smaller answer, it is a WRONG answer, and it should say so on screen rather
 * than wait for someone to notice by feel months later.
 */
export function TruncationNotice({
  total,
  returned,
  truncated,
  noun = 'customers',
}: {
  /** Rows MATCHING on the server. */
  total?: number;
  /** Rows actually delivered. */
  returned?: number;
  /** Server's own verdict — preferred over re-deriving it here. */
  truncated?: boolean;
  noun?: string;
}) {
  // Older responses (or a cached one) may not carry the new fields. Absent
  // information is not evidence of truncation, so render nothing rather than
  // guessing — a false warning would train people to ignore the real one.
  if (total == null || returned == null) return null;
  const isCut = truncated ?? returned < total;
  if (!isCut) return null;

  return (
    <div
      className="banner banner-warn"
      data-testid="truncation-notice"
      role="alert"
    >
      Showing <strong>{returned.toLocaleString()}</strong> of{' '}
      <strong>{total.toLocaleString()}</strong> {noun}. Totals and counts on
      this page describe only the {returned.toLocaleString()} loaded, not the
      full book.
    </div>
  );
}

export default TruncationNotice;
